// frontend/src/pages/Browse.js
import React, { useState, useEffect } from 'react';
import { Link } from '../routing';
import api from '../utils/api';
import { useTranslation } from 'react-i18next';
import { PLACEHOLDER_IMG } from '../config';
import AIRecommender from '../components/AIRecommender';
import PageHero from '../components/PageHero';
import { Reveal, Tilt3D } from '../components/Motion';
import theme from '../theme';

// The self-hosted copy in public/fonts, referenced once from index.html.
// This used to be a Google Fonts URL injected per page, which disclosed every
// visitor's IP to a third party on every page load and contradicted the privacy
// policy's "no third-party requests" claim. Kept as a constant because several
// pages import it into an @import rule rather than a <link>.
const FONTS = '/fonts/fonts.css';

export default function Browse() {
  const { t } = useTranslation();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  // Distinguishes "the catalogue is empty" from "we could not reach the server".
  const [loadError, setLoadError] = useState(false);
  const [filterTexture, setFilterTexture] = useState('all');
  const [filterColor, setFilterColor]     = useState('all');
  const [filterAvail, setFilterAvail]     = useState(false);

  useEffect(() => {
    const link = document.createElement('link');
    link.rel = 'stylesheet'; link.href = FONTS;
    document.head.appendChild(link);
    // Without the catch, a backend outage rendered the "no results" empty state —
    // the catalogue page telling a paying customer GlamGo has nothing to rent.
    api.get('/products')
      .then(setProducts)
      .catch(() => setLoadError(true))
      .finally(() => setLoading(false));
    return () => document.head.removeChild(link);
  }, []);

  // Built from the vocabulary KEYS the API now sends, and labelled from the i18n
  // bundle. When these were free text the filter list was whatever operators had
  // typed — "Dark Brown / Chocolate" and "Chocolate Brown" became two filters
  // over one shelf — and translating the label would have broken matching
  // outright, because the value being compared was the label itself.
  const textures = ['all', ...new Set(products.map(p => p.texture_key).filter(Boolean))];
  const colors   = ['all', ...new Set(products.map(p => p.color_key).filter(Boolean))];

  const filtered = products.filter(p => {
    if (filterTexture !== 'all' && p.texture_key !== filterTexture) return false;
    if (filterColor !== 'all' && p.color_key !== filterColor) return false;
    return true;
  });

  // One card per COLOUR, not per physical set.
  //
  // Stock is one row per set, because that is what the cabinet holds: each has
  // its own compartment, its own status and its own history. The customer does
  // not care which of the three identical black sets they get — showing them as
  // three cards makes a six-colour catalogue look like eighteen products and
  // makes the shelf look padded.
  //
  // Grouped on everything that would make two rows a different offer to a buyer.
  // Two sets that differ in length are NOT the same product even in the same
  // colour, and merging them would advertise a length we might not be able to
  // hand over.
  const groups = (() => {
    const byKey = new Map();
    for (const p of filtered) {
      const key = [p.color_key, p.texture_key, p.length_cm, p.base_price_per_day].join('|');
      if (!byKey.has(key)) byKey.set(key, []);
      byKey.get(key).push(p);
    }
    const built = [...byKey.values()].map(units => {
      const available = units.filter(u => u.status === 'available');
      return {
        // The card links to a unit that can actually be rented, so the customer
        // does not land on a detail page whose button is disabled while two
        // identical sets sit free beside it. Falls back to the first unit so a
        // fully-rented colour still has a page to show.
        representative: available[0] || units[0],
        availableCount: available.length,
        totalCount: units.length,
      };
    });
    // Applied here rather than to the rows above so the counts stay honest: a
    // colour with one set free out of three has to be able to say so.
    return filterAvail ? built.filter(g => g.availableCount > 0) : built;
  })();

  const hasActiveFilters = filterTexture !== 'all' || filterColor !== 'all' || filterAvail;
  const clearFilters = () => { setFilterTexture('all'); setFilterColor('all'); setFilterAvail(false); };

  return (
    <div style={{ minHeight: '100vh', background: theme.cream, fontFamily: theme.font.body }}>
      <PageHero
        eyebrow={t('browse.collectionLabel')}
        title={t('browse.title')}
        subtitle={t('browse.subtitle')}
      />
      <div style={{ maxWidth: 1200, margin: '0 auto', padding: '56px 24px 80px' }}>

        {/* AI Recommender */}
        <Reveal>
          <AIRecommender />
        </Reveal>

        {/* Filters */}
        <Reveal delay={0.08} style={{ marginBottom: 48 }}>
          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', justifyContent: 'center', marginBottom: 12 }}>
            {textures.map(tx => (
              <button key={tx} onClick={() => setFilterTexture(tx)} style={chipStyle(filterTexture === tx)}>
                {tx === 'all' ? t('browse.allStyles') : t(`catalog.texture.${tx}`, { defaultValue: tx })}
              </button>
            ))}
          </div>
          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', justifyContent: 'center', alignItems: 'center' }}>
            {colors.map(c => (
              <button key={c} onClick={() => setFilterColor(c)} style={chipStyle(filterColor === c, theme.gold)}>
                {c === 'all' ? t('browse.allColors') : t(`catalog.color.${c}`, { defaultValue: c })}
              </button>
            ))}
            <button onClick={() => setFilterAvail(!filterAvail)} style={chipStyle(filterAvail, theme.green)}>
              {t('browse.availableOnly')}
            </button>
            {hasActiveFilters && (
              <button onClick={clearFilters} style={{ padding: '8px 18px', borderRadius: 100, border: `1px solid ${theme.red}`, background: 'transparent', color: theme.red, fontSize: 12, cursor: 'pointer', fontFamily: theme.font.body }}>
                {t('browse.clear')}
              </button>
            )}
          </div>
          {hasActiveFilters && (
            <p style={{ textAlign: 'center', color: theme.grey400, fontSize: 13, marginTop: 12 }}>
              {groups.length} {t('browse.results')}
            </p>
          )}
        </Reveal>

        {/* Grid */}
        {loading ? (
          <div style={{ textAlign: 'center', padding: 80 }}>
            <div style={{ fontFamily: theme.font.display, fontSize: 24, color: theme.gold }}>{t('browse.loading')}</div>
          </div>
        ) : (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(min(300px, 100%), 1fr))', gap: 32 }}>
            {/* The stagger is capped: past the first row or two the delay stops
                being a flourish and starts being a wait. */}
            {groups.map((group, i) => (
              <Reveal key={group.representative.id} delay={Math.min(i, 5) * 0.07}>
                <Tilt3D max={7}>
                  <ProductCard
                    product={group.representative}
                    availableCount={group.availableCount}
                    totalCount={group.totalCount}
                  />
                </Tilt3D>
              </Reveal>
            ))}
          </div>
        )}

        {loadError && !loading && (
          <div style={{ textAlign: 'center', padding: 80, color: theme.grey400 }}>
            <p style={{ marginBottom: 16 }}>{t('browse.loadError')}</p>
            <button
              onClick={() => window.location.reload()}
              style={{ padding: '10px 20px', background: theme.ink, color: theme.white, border: 'none', borderRadius: 2, fontSize: 12, cursor: 'pointer', fontFamily: theme.font.body }}>
              {t('browse.retry')}
            </button>
          </div>
        )}

        {groups.length === 0 && !loading && !loadError && (
          <div style={{ textAlign: 'center', padding: 80, color: theme.grey400 }}>{t('browse.noResults')}</div>
        )}
      </div>
    </div>
  );
}

function chipStyle(active, activeColor = theme.ink) {
  return {
    padding: '8px 20px', borderRadius: 100,
    border: active ? 'none' : `1px solid ${theme.sandMid}`,
    background: active ? activeColor : 'transparent',
    color: active ? theme.white : theme.grey600,
    fontSize: 13, letterSpacing: '0.08em', cursor: 'pointer',
    fontFamily: theme.font.body, transition: 'all 0.2s'
  };
}

function ProductCard({ product, availableCount = 0, totalCount = 1 }) {
  const { t } = useTranslation();
  const available = availableCount > 0;
  const img = product.image_urls?.[0] || PLACEHOLDER_IMG;

  return (
    <Link to={`/product/${product.id}`} style={{ textDecoration: 'none' }}>
      <div style={{
        background: theme.white, borderRadius: 4, overflow: 'hidden',
        boxShadow: `0 2px 20px ${theme.alpha.black(0.06)}`,
        transition: 'box-shadow 0.3s',
        cursor: 'pointer', height: '100%'
      }}
        // The lift used to be a translateY here; the card now sits inside
        // Tilt3D, and two components writing `transform` on the same hover
        // fight each other. Tilt3D owns the movement, this owns the shadow.
        onMouseEnter={e => { e.currentTarget.style.boxShadow = `0 18px 50px ${theme.alpha.black(0.16)}`; }}
        onMouseLeave={e => { e.currentTarget.style.boxShadow = `0 2px 20px ${theme.alpha.black(0.06)}`; }}
      >
        <div style={{ position: 'relative', height: 340, overflow: 'hidden' }}>
          <img src={img} alt={product.name} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />

          <div style={{
            position: 'absolute', top: 16, left: 16,
            background: available ? theme.alpha.white(0.95) : theme.alpha.black(0.7),
            color: available ? theme.green : theme.white,
            padding: '5px 14px', borderRadius: 100, fontSize: 11, letterSpacing: '0.1em',
            fontWeight: 500
          }}>
            {available ? t('browse.available') : t('browse.unavailable')}
          </div>

          <div style={{
            position: 'absolute', bottom: 16, right: 16,
            background: theme.alpha.gold(0.95), color: theme.white,
            padding: '6px 14px', borderRadius: 100, fontSize: 12
          }}>
            {product.length_cm}cm
          </div>
        </div>

        <div style={{ padding: '24px' }}>
          <h3 style={{
            fontFamily: theme.font.display, fontSize: 22,
            color: theme.ink, margin: '0 0 6px', fontWeight: 500
          }}>{product.name}</h3>
          <p style={{ color: theme.grey400, fontSize: 13, margin: '0 0 16px' }}>{t(`catalog.color.${product.color_key}`, { defaultValue: product.color_key })}</p>

          {/* How many of this colour are free right now. Stated because "3
              available" and "the last one" are different decisions, and the
              customer has no other way to tell — the three sets behind this card
              are deliberately indistinguishable to them. */}
          {totalCount > 1 && (
            <p style={{ color: available ? theme.green : theme.grey400, fontSize: 12, margin: '0 0 14px', letterSpacing: '0.03em' }}>
              {available
                ? t('browse.availableOfTotal', { count: availableCount, total: totalCount })
                : t('browse.allRented', { count: totalCount })}
            </p>
          )}

          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div>
              <span style={{ fontFamily: theme.font.display, fontSize: 24, color: theme.ink, fontWeight: 500 }}>€{product.base_price_per_day}</span>
              <span style={{ color: theme.grey400, fontSize: 13 }}>{t('browse.perDay')}</span>
            </div>
            <div style={{ color: theme.taupe, fontSize: 12 }}>{t('browse.deposit')} €{product.deposit_amount}</div>
          </div>

          <div style={{
            marginTop: 20, display: 'flex', justifyContent: 'center',
            padding: '14px', background: available ? theme.ink : theme.grey120,
            borderRadius: 2, color: available ? theme.white : theme.grey400,
            fontSize: 12, letterSpacing: '0.15em', fontWeight: 500
          }}>
            {available ? t('browse.selectRent') : t('browse.currentlyRented')}
          </div>
        </div>
      </div>
    </Link>
  );
}
